<script setup>
defineProps({
  card: Array,
})
</script>

<template>
  <div class="grid grid-cols-1 gap-custom-md md:grid-cols-2 xl:grid-cols-3 xl:gap-custom-lg">
    <NuxtLink
      v-for="(item, index) in card"
      :key="item._uid"
      :to="localePath({
        path: rootedPath(item.link?.cached_url || item.link?.url || ''),
      })"
      :style="{
        animationDelay: `${index * 90}ms`,
      }"
      class="card-image group transition-[box-shadow,transform] relative min-h-[240px] overflow-hidden rounded-md bg-secondary-white shadow-custom-xl shadow-shadow-light/10 duration-500 ease-out lg:min-h-[320px] md:min-h-[300px] sm:min-h-[260px] hover:shadow-custom-3xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-secondary-red-100/30 xl:hover:-translate-y-2"
    >
      <nuxt-img
        v-if="item.media?.filename"
        :src="`${item.media.filename}/m/650x520/filters:quality(82)`"
        :alt="item.media?.alt || item.title"
        width="650"
        height="520"
        loading="lazy"
        sizes="sm:100vw md:50vw xl:33vw"
        class="absolute inset-0 h-full w-full object-cover transition duration-700 ease-out group-focus-visible:scale-110 group-hover:scale-110"
      />
      <div class="absolute inset-0 from-primary/90 via-primary/42 to-transparent bg-gradient-to-t transition-opacity duration-500 ease-out group-focus-visible:opacity-100 group-hover:opacity-100" />
      <div class="card-image-shine transition-[opacity,transform] pointer-events-none absolute inset-y-0 left-[-75%] z-5 w-1/2 rotate-12 bg-white/18 opacity-0 blur-xl duration-700 ease-out group-focus-visible:translate-x-[360%] group-hover:translate-x-[360%] group-focus-visible:opacity-100 group-hover:opacity-100" />
      <div class="absolute inset-0 opacity-0 transition duration-300 group-focus-visible:opacity-100 group-hover:opacity-100">
        <div class="absolute inset-x-0 top-0 h-px from-transparent via-white/45 to-transparent bg-gradient-to-r" />
        <div class="absolute inset-y-0 right-0 w-px from-transparent via-secondary-red-100/70 to-transparent bg-gradient-to-b" />
      </div>

      <div class="relative z-10 h-full min-h-[240px] flex flex-col justify-end p-5 lg:min-h-[320px] md:min-h-[300px] sm:min-h-[260px] md:p-7 sm:p-6">
        <div class="card-image-line mb-2 h-1 w-10 rounded-full bg-secondary-red-100 transition-transform duration-500 ease-out sm:w-12 group-focus-visible:-translate-y-1 group-hover:-translate-y-1" />
        <h3 class="card-image-title w-full text-[16px] text-white font-semibold leading-[1.18] capitalize transition-transform duration-500 ease-out lg:text-[22px] md:text-[20px] sm:text-[18px] group-focus-visible:-translate-y-1 group-hover:-translate-y-1">
          {{ item.title }}
        </h3>

        <div class="transition-[opacity,transform] mt-3 flex translate-y-0 items-center gap-1 text-sm text-white font-medium opacity-100 duration-500 delay-75 ease-out sm:mt-2 xl:mt-2 group-focus-visible:translate-y-0 group-hover:translate-y-0 xl:translate-y-3 md:text-md group-focus-visible:opacity-100 group-hover:opacity-100 xl:opacity-0">
          <ul v-if="item?.actions?.length" class="m-0 p-0">
            <li v-for="action in item?.actions" :key="action.uuid" class="m-0 [&_span]:!p-0 [&_*]:!text-white [&_span]:!ring-0">
              <StoryblokComponent :blok="action" />
            </li>
          </ul>
          <div class="translate-x-0 text-white transition-transform duration-300 ease-out group-focus-visible:translate-x-1.5 group-hover:translate-x-1.5">
            <svg class="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M9.29 15.88L13.17 12L9.29 8.12a.996.996 0 1 1 1.41-1.41l4.59 4.59c.39.39.39 1.02 0 1.41L10.7 17.3a.996.996 0 0 1-1.41 0c-.38-.39-.39-1.03 0-1.42z" /></svg>
          </div>
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<style scoped>
.card-image {
  animation: card-image-in 600ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.card-image-line {
  box-shadow: 0 0 20px rgba(250, 34, 52, 0.45);
}

.card-image-title {
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.48);
}

@keyframes card-image-in {
  0% {
    opacity: 0;
    transform: translateY(22px) scale(0.98);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .card-image {
    animation: none;
  }

  .card-image,
  .card-image * {
    transition-duration: 1ms !important;
  }
}
</style>
